import React, { useEffect } from 'react';
import { getCurrentSearch } from '../../api/internal/usersInternalApi';
import { choiceColumns } from '../../utils/tanstack-table';
import { Card, EmptyState, Spinner, SpinnerContainer } from '../ui';
import Table from './table';
import { useDispatch, useSelector } from 'react-redux';
import { setColumnsType, setServerLimit, setServerPage, setServerSorting, setSavedFilters } from '../../redux/advancedSearch/advancedSearchSlice';

const DEFAULT_PAGINATION = {
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
};

const DEFAULT_SORTING = {
    sortField: 'displayName',
    sortOrder: 'asc',
};

const SMALL_SCREEN_WIDTH = 1600;

// Colonnes masquées par défaut, par type de recherche. Les clés sont les id de colonnes
// TanStack : l'accessorKey, ou le header quand la colonne utilise un accessorFn (ex. "ID").
const HIDDEN_COLUMNS = {
    Users: ['ID', 'manager', 'streetAddress', 'postalCode', 'mobilePhone', 'givenName', 'surname', 'accountEnabled'],
    Groups: ['ID'],
    Devices: ['ID'],
    Applications: ['objectId', 'appRoleId'],
};

// Colonnes masquées en plus sur les écrans étroits
const HIDDEN_COLUMNS_SMALL_SCREEN = {
    Users: ['city', 'jobTitle', 'employeeId'],
    Groups: ['visibility'],
    Devices: [],
    Applications: ['Utilisateurs rattachés'],
};

function buildColumnVisibility(type) {
    const hidden = [
        ...(HIDDEN_COLUMNS[type] || []),
        ...(window.innerWidth <= SMALL_SCREEN_WIDTH ? (HIDDEN_COLUMNS_SMALL_SCREEN[type] || []) : []),
    ];
    return Object.fromEntries(hidden.map(id => [id, false]));
}

export default function SearchListResult({ filters, information }) {
    const dispatch = useDispatch();

    const advancedSearchState = useSelector((state) => state.advancedSearch) || {};
    const {
        currentSearch = null,
        isAdvancedSearchLoading = false,
        isPageLoading = false,
        columnsType = null,
        savedFilters = null,
    } = advancedSearchState;

    const serverPagination = advancedSearchState.serverPagination || DEFAULT_PAGINATION;
    const serverSorting = advancedSearchState.serverSorting || DEFAULT_SORTING;

    const currentLimit = serverPagination.limit ?? 20;
    const currentSortField = serverSorting.sortField ?? 'displayName';
    const currentSortOrder = serverSorting.sortOrder ?? 'asc';

    // On utilise les filtres de Redux s'ils existent (navigation), sinon ceux des props (nouvelle recherche)
    const filtersToUse = (savedFilters && savedFilters.length > 0) ? savedFilters : filters;

    // Détermine le type d'info (Users, Devices...) basé sur les filtres ou la prop
    const currentInformation = filtersToUse?.[0]?.information || information || 'Users';

    // Helper pour vérifier si les filtres sont valides (au moins une ligne avec une propriété sélectionnée)
    const areFiltersValid = (f) => {
        return (
            Array.isArray(f) &&
            f.some(row =>
                row.propertie &&
                row.input !== null &&
                row.input !== undefined &&
                row.input !== ''
            )
        );
    };


    // Sauvegarder les filtres dans Redux quand ils arrivent via Props et sont valides
    useEffect(() => {
        // On ne sauvegarde que si les filtres viennent des props (donc potentiellement nouveaux)
        if (filters && areFiltersValid(filters)) {
            // Comparaison simple pour éviter boucle infinie
            if (JSON.stringify(filters) !== JSON.stringify(savedFilters)) {
                dispatch(setSavedFilters(filters));
                dispatch(setColumnsType(currentInformation));
            }
        }
    }, [filters, dispatch]);

    // Fetch initial (seulement si pas de cache valide)
    useEffect(() => {
        const controller = new AbortController();
        
        // Si on a déjà les résultats en cache pour cette recherche précise, on ne refait pas le fetch
        // (Optionnel : vous pouvez retirer cette condition si vous voulez forcer le refresh)
        const isCacheValid = currentSearch && columnsType === currentInformation && savedFilters === filtersToUse;

        if (!isCacheValid && areFiltersValid(filtersToUse)) {
            dispatch(setColumnsType(currentInformation));
            
            getCurrentSearch(
                dispatch,
                filtersToUse,
                controller.signal,
                1,
                serverPagination.limit || 20,
                { sortField: serverSorting.sortField || 'displayName', sortOrder: serverSorting.sortOrder || 'asc' },
                true
            );
        }
        
        return () => controller.abort();
    }, [filters]);

    // Fonction de fetch réutilisable pour la pagination/tri
    const fetchPage = (page, limit, sortField, sortOrder) => {
        if (!areFiltersValid(filtersToUse)) {
            console.error('ERREUR: Pas de filtres valides pour la pagination !');
            return null;
        }

        const controller = new AbortController();
        
        getCurrentSearch(
            dispatch,
            filtersToUse,
            controller.signal,
            page,
            limit,
            { sortField, sortOrder },
            false
        );
        
        return controller;
    };

    // Handlers
    const handlePageChange = (newPage) => {
        dispatch(setServerPage(newPage));
        fetchPage(newPage, currentLimit, currentSortField, currentSortOrder);
    };

    const handlePageSizeChange = (newSize) => {
        dispatch(setServerLimit(newSize));
        fetchPage(1, newSize, currentSortField, currentSortOrder);
    };

    const handleSortingChange = (sortField, sortOrder) => {
        dispatch(setServerSorting({ sortField, sortOrder }));
        fetchPage(1, currentLimit, sortField, sortOrder);
    };

    // Loader initial
    if (isAdvancedSearchLoading && !currentSearch) {
        return (
            <Card>
                <SpinnerContainer><Spinner /></SpinnerContainer>
            </Card>
        );
    }

    // Récupération des colonnes dynamiques
    // Note: On utilise currentInformation qui vient du filtre pour être sûr d'avoir les bonnes colonnes
    const columns = choiceColumns(columnsType || currentInformation);
    
    if (!columns || !currentSearch) {
        // État vide ou chargement si pas de données
        return (
            <Card>
                {isAdvancedSearchLoading ? (
                    <SpinnerContainer><Spinner /></SpinnerContainer>
                ) : (
                    <EmptyState>
                        <i className="bx bx-search-alt-2" />
                        <h3>Aucun résultat</h3>
                        <p>Aucune donnée ne correspond à ces filtres.</p>
                    </EmptyState>
                )}
            </Card>
        );
    }

    return (
        <Card className="search-result" $pad="12px 16px">
            <Table
                key={columnsType || information}
                columns={columns}
                data={currentSearch || []}
                colVisibility={buildColumnVisibility(columnsType || currentInformation)}
                columnsType={columnsType || currentInformation}
                serverPagination={serverPagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onSortingChange={handleSortingChange}
                filters={filtersToUse}
                isPageLoading={isPageLoading}
            />
        </Card>
    );
}